import { useMemo } from 'react';
import { useRef } from 'react';
import ChartWrapper, {
  ChartWrapperRef,
} from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { shallowEqual, useSelector } from 'react-redux';
import { COMPANY_INTRINSIC_VALUE_CALC } from '@/constants/feedbackSections';
import { selectDcfData } from '../..';
import { DCFRawDataModal } from '@/pages/CompanyReport/components/Value/';
import loadable from '@loadable/component';
import { SWS_SUPPORT } from '@/constants';
import { Button } from '@simplywallst/ui-core';

const DCF = loadable(() => import('@components/DCF'), { ssr: false });

export const SharePriceVsFairValue = () => {
  const ChartWrapperRef = useRef<ChartWrapperRef>(null);

  const dcfData = useSelector<
    Parameters<typeof selectDcfData>[0],
    ReturnType<typeof selectDcfData>
  >(selectDcfData, shallowEqual);
  const helpContent = useMemo(
    () => (
      <div>
        <p>
          The discounted cash flow is a fundamental valuation technique to
          estimate intrinsic value. The technique involves forecasting how a
          company might grow and what that means for the free cash flow of the
          business.
        </p>
        <p>
          It then uses a discount rate to represent those future cash flows in
          today’s terms. This accounts for the fact that our estimations of
          future cash flows are risky. Higher discount rates indicate higher
          risk.
        </p>
        <p>Depending on the company, we use a different DCF method.</p>
        <p>
          You can read more about our methodology and see how the model works in
          our{' '}
          <Button
            as="a"
            target="_blank"
            href={
              SWS_SUPPORT +
              '/articles/4751563581071-Understanding-the-Valuation-section-in-the-company-report#Discounted-Cash-Flow'
            }
            rel="noopener noreferrer"
            styleType="strongLink"
          >
            Help Centre.
          </Button>
        </p>
      </div>
    ),
    []
  );
  return (
    <ChartWrapper
      ref={ChartWrapperRef}
      id="dcf-chart"
      chart={
        dcfData.intrinsicValue <= 0 ? (
          <DCF
            sharePrice={50}
            sharePriceFormatted={''}
            intrinsicValue={60}
            intrinsicValueFormatted={''}
            intrinsicDiscount={0.5}
            intrinsicDiscountFormatted={''}
            showNewStyle={true}
            showEmptyState
          />
        ) : (
          <DCF
            sharePrice={dcfData.sharePrice}
            sharePriceFormatted={dcfData.sharePriceFormatted}
            intrinsicValue={dcfData.intrinsicValue}
            intrinsicValueFormatted={dcfData.intrinsicValueFormatted}
            intrinsicDiscount={dcfData.intrinsicDiscount}
            intrinsicDiscountFormatted={dcfData.intrinsicDiscountFormatted}
            showNewStyle={true}
          />
        )
      }
      help={helpContent}
      helpTitle="Share Price vs Fair Value"
      rawData={
        dcfData.intrinsicValue > 0 ? (
          <DCFRawDataModal
            onClose={() => ChartWrapperRef.current?.showChart(true)}
            companyId={dcfData.companyId}
          />
        ) : undefined
      }
      preRenderHeight={380}
      preRenderHeightMdDown={370}
      actionsMarginTop={8}
      feedbackLabel={COMPANY_INTRINSIC_VALUE_CALC}
    />
  );
};
